import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  createDefaultOutputImageSide,
  createDefaultTransformation,
  defaultAiTransform,
} from "../../common/slices/specsEditor/reducers/images";
import { Input } from "../../common/components/Input";
import { palette } from "../../styles/theme";
import {
  AiTransform,
  AI_PROMPT_SUGGESTIONS,
  OutputImage,
  OutputImageRole,
  SpecsImageProcessingStatus,
  Transformation,
} from "./types";
import { resolveSpecsImageUrl } from "./specsAssetUrls";
import { SpecsImageAdjustmentsPanel } from "./SpecsImageAdjustmentsPanel";
import { useImageProcessing } from "./useImageProcessing";
import {
  type SpecsImageUploadContext,
  uploadSpecsImageUrl,
} from "./specsImageUpload";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsEditorFalCalls } from "./useSpecsEditorFalCalls";

interface AiTransformModalProps {
  open: boolean;
  label: string;
  role: OutputImageRole;
  sourceImageUrl: string | null;
  fileNamePrefix: string;
  uploadContext: SpecsImageUploadContext;
  sourceOptions?: Array<{ label: string; imageUrl: string }>;
  selectedSourceUrl?: string;
  onClose: () => void;
  onSelectSource?: (sourceUrl: string) => void;
  onChange: (url: string | null) => void;
}

function createOutputImage(
  id: string,
  label: string,
  role: OutputImageRole,
  imageUrl: string | null,
  transformations: Transformation[],
  aiTransform: AiTransform
): OutputImage {
  const side = {
    sourceLayerId: null,
    referenceImageIds: [],
    transformations,
    aiTransform,
    outputImageUrl: imageUrl,
  };

  return {
    id,
    name: label,
    role,
    sourceLayerId: side.sourceLayerId,
    referenceImageIds: side.referenceImageIds,
    backgroundColor: null,
    transformations: side.transformations,
    aiTransform,
    outputImageUrl: imageUrl,
    sides: { front: side },
    back: null,
  };
}

export function AiTransformModal({
  open,
  label,
  role,
  sourceImageUrl,
  fileNamePrefix,
  uploadContext,
  sourceOptions = [],
  selectedSourceUrl = "",
  onClose,
  onSelectSource,
  onChange,
}: AiTransformModalProps) {
  const { editor } = useSpecsEditorContextValue();
  const { callFal } = useSpecsEditorFalCalls();
  const [selectedGenId, setSelectedGenId] = useState<string | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [aiTransform, setAiTransform] = useState(defaultAiTransform);
  const [transformations, setTransformations] = useState(
    () => createDefaultOutputImageSide(role).transformations
  );
  const [backgroundColor, setBackgroundColor] = useState<string | null>(null);
  const [applyFiltersLoading, setApplyFiltersLoading] = useState(false);
  const processedImageUrl = useImageProcessing(sourceImageUrl, transformations);
  const hasPendingFilters = transformations.some(
    (transformation) => transformation.enabled
  );
  const addedTypes = React.useMemo(
    () => new Set(transformations.map((transformation) => transformation.type)),
    [transformations]
  );
  const outputImage = React.useMemo(
    () =>
      createOutputImage(
        `${fileNamePrefix}-picker`,
        label,
        role,
        processedImageUrl ?? sourceImageUrl,
        transformations,
        aiTransform
      ),
    [
      aiTransform,
      fileNamePrefix,
      label,
      processedImageUrl,
      role,
      sourceImageUrl,
      transformations,
    ]
  );
  const isProcessing =
    aiTransform.status === SpecsImageProcessingStatus.PROCESSING;
  const hasAccepted = !!aiTransform.acceptedImageUrl;
  const generations = aiTransform.generations ?? [];
  const latestGen = generations[0] ?? null;
  const viewedGen = selectedGenId
    ? (generations.find((g) => g.id === selectedGenId) ?? latestGen)
    : latestGen;
  const nextSourceUrl = aiTransform.acceptedImageUrl ?? sourceImageUrl;
  const previewSourceUrl = hasPendingFilters
    ? processedImageUrl
    : nextSourceUrl;
  const aiSourceUrl = previewSourceUrl ?? nextSourceUrl;
  const resolvedPreviewSourceUrl = resolveSpecsImageUrl(previewSourceUrl);
  const resolvedViewedGenUrl = resolveSpecsImageUrl(viewedGen?.imageUrl);
  const adjustments = (
    <SpecsImageAdjustmentsPanel
      transformations={transformations}
      addedTypes={addedTypes}
      backgroundColor={backgroundColor}
      disabled={isProcessing}
      onAddTransformation={(type) => {
        setTransformations((current) => [
          ...current,
          createDefaultTransformation(type),
        ]);
      }}
      onRemoveTransformation={(transformId) => {
        setTransformations((current) =>
          current.filter((transformation) => transformation.id !== transformId)
        );
      }}
      onUpdateTransformation={(transformId, patch) => {
        setTransformations((current) =>
          current.map((transformation) =>
            transformation.id === transformId
              ? { ...transformation, ...patch }
              : transformation
          )
        );
      }}
      onUpdateBackgroundColor={setBackgroundColor}
    />
  );

  const updateAiTransform = (patch: Partial<AiTransform>) => {
    setAiTransform((current) => ({ ...current, ...patch }));
  };
  const appendToPrompt = (text: string) => {
    const current = aiTransform.prompt.trim();
    updateAiTransform({ prompt: current ? `${current}\n${text}` : text });
  };
  const requestClose = () => {
    if (hasPendingFilters) {
      setConfirmDiscardOpen(true);
      return;
    }

    onClose();
  };
  const addAiGeneration = (prompt: string, imageUrl: string) => {
    setAiTransform((current) => ({
      ...current,
      status: SpecsImageProcessingStatus.DONE,
      errorMessage: undefined,
      generations: [
        {
          id: `${Date.now()}`,
          prompt,
          imageUrl,
          createdAt: new Date().toISOString(),
        },
        ...current.generations,
      ],
    }));
  };
  const runAiTransform = async () => {
    const sourceUrl = aiSourceUrl;
    const prompt = aiTransform.prompt.trim();
    if (!sourceUrl || !prompt) {
      return;
    }

    updateAiTransform({
      status: SpecsImageProcessingStatus.PROCESSING,
      errorMessage: undefined,
    });

    try {
      let resultUrl: string | undefined;
      if (aiTransform.method === "sam3") {
        const { callSam3 } = await import("./callSam3");
        const result = await callSam3(
          sourceUrl,
          prompt,
          editor.session.falSettings.apiKey,
          { applyMask: false }
        );
        resultUrl = result.masks[0]?.url;
      } else {
        const result = await callFal(
          prompt,
          [sourceUrl],
          editor.session.falSettings
        );
        resultUrl = result?.images?.[0]?.url;
      }

      if (!resultUrl) {
        return;
      }

      addAiGeneration(prompt, resultUrl);
    } catch (error) {
      updateAiTransform({
        status: SpecsImageProcessingStatus.ERROR,
        errorMessage: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };
  const acceptAiGeneration = async (generationId: string) => {
    const generation = aiTransform.generations.find(
      (item) => item.id === generationId
    );
    if (!generation) {
      throw new Error(`Missing AI generation "${generationId}".`);
    }

    updateAiTransform({
      status: SpecsImageProcessingStatus.PROCESSING,
      errorMessage: undefined,
    });

    try {
      const uploadedUrl = await uploadSpecsImageUrl(
        generation.imageUrl,
        fileNamePrefix,
        uploadContext
      );
      setAiTransform((current) => ({
        ...current,
        status: SpecsImageProcessingStatus.DONE,
        acceptedImageUrl: uploadedUrl,
        errorMessage: undefined,
        generations: current.generations.map((item) =>
          item.id === generationId ? { ...item, imageUrl: uploadedUrl } : item
        ),
      }));
      onChange(uploadedUrl);
      onClose();
    } catch (error) {
      updateAiTransform({
        status: SpecsImageProcessingStatus.ERROR,
        errorMessage:
          error instanceof Error ? error.message : "Failed to upload AI image",
      });
    }
  };
  const applyFilteredImage = async () => {
    if (!processedImageUrl) {
      return;
    }

    setApplyFiltersLoading(true);
    try {
      const uploadedUrl = await uploadSpecsImageUrl(
        processedImageUrl,
        fileNamePrefix,
        uploadContext,
        {
          allowedContentTypes: ["image/png"],
          normalizeOversizedRaster: false,
        }
      );
      onChange(uploadedUrl);
      setTransformations([]);
      updateAiTransform({
        status: SpecsImageProcessingStatus.IDLE,
        errorMessage: undefined,
      });
      onClose();
    } catch (error) {
      updateAiTransform({
        status: SpecsImageProcessingStatus.ERROR,
        errorMessage:
          error instanceof Error
            ? error.message
            : "Failed to upload filtered image",
      });
    } finally {
      setApplyFiltersLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{ "& .MuiDialog-paper": { maxHeight: "90vh" } }}
      >
        <DialogTitle>AI Transform — {outputImage.name}</DialogTitle>
        <DialogContent dividers sx={{ overflowY: "auto" }}>
          {previewSourceUrl && (
            <>
              <Box sx={{ mb: hasAccepted ? 0 : 2 }}>
                <Typography
                  variant="caption"
                  sx={{ mb: 0.5, display: "block" }}
                >
                  {hasAccepted
                    ? "Input for next generation (accepted AI result)"
                    : "Input for next generation (source layer)"}
                </Typography>
                {!hasAccepted && sourceOptions.length > 0 && onSelectSource ? (
                  <Select
                    displayEmpty
                    size="small"
                    fullWidth
                    value={selectedSourceUrl}
                    onChange={(event) => {
                      const sourceUrl = event.target.value;
                      if (sourceUrl) {
                        onSelectSource(sourceUrl);
                      }
                    }}
                    sx={{
                      mb: 1,
                      ".MuiSelect-select": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <Typography variant="body2" color="text.secondary">
                        Select source
                      </Typography>
                    </MenuItem>
                    {sourceOptions.map((source) => (
                      <MenuItem key={source.imageUrl} value={source.imageUrl}>
                        {source.label}
                      </MenuItem>
                    ))}
                  </Select>
                ) : null}
                <Box
                  component="img"
                  src={resolvedPreviewSourceUrl}
                  alt="Next generation source"
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 180,
                    objectFit: "contain",
                    borderRadius: "4px",
                    bgcolor: palette.grey.lightBackground,
                    border: hasAccepted
                      ? `2px solid ${palette.brand.primary}`
                      : "none",
                  }}
                />
              </Box>
              {hasAccepted && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    updateAiTransform({
                      status: SpecsImageProcessingStatus.IDLE,
                      acceptedImageUrl: null,
                      errorMessage: undefined,
                    });
                    onChange(null);
                  }}
                  sx={{ mb: 2 }}
                >
                  Reset to source layer
                </Button>
              )}
            </>
          )}

          {adjustments && <Box sx={{ mb: 2 }}>{adjustments}</Box>}

          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Method
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={aiTransform.method ?? "gpt-image-2"}
              onChange={(_, value) => {
                if (value) updateAiTransform({ method: value });
              }}
              sx={{ height: 28 }}
            >
              <ToggleButton
                value="gpt-image-2"
                sx={{
                  fontSize: "0.75rem",
                  px: 1.5,
                  textTransform: "none",
                }}
              >
                GPT Image 2
              </ToggleButton>
              <ToggleButton
                value="sam3"
                sx={{
                  fontSize: "0.75rem",
                  px: 1.5,
                  textTransform: "none",
                }}
              >
                SAM3
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack
            direction="row"
            alignItems="flex-start"
            gap={1}
            sx={{ mb: 1.5 }}
          >
            <Input
              label="Prompt"
              multiline
              fullWidth
              noMaxWidth
              value={aiTransform.prompt}
              onChange={(e) => updateAiTransform({ prompt: e.target.value })}
              placeholder="Describe what you want the AI to do with this image..."
              inputProps={{ style: { minHeight: 80 } }}
            />
            {aiTransform.prompt && (
              <IconButton
                size="small"
                onClick={() => updateAiTransform({ prompt: "" })}
                sx={{ mt: 1 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
            Quick prompts
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {AI_PROMPT_SUGGESTIONS.map((suggestion) => (
              <Button
                key={suggestion.label}
                variant="outlined"
                size="small"
                onClick={() => appendToPrompt(suggestion.prompt)}
              >
                {suggestion.label}
              </Button>
            ))}
          </Stack>

          <Button
            variant="outlined"
            onClick={() => {
              void runAiTransform();
            }}
            disabled={
              isProcessing || !aiTransform.prompt.trim() || !aiSourceUrl
            }
            startIcon={
              isProcessing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoFixHighIcon />
              )
            }
            fullWidth
            sx={{ mb: 2 }}
          >
            {isProcessing
              ? "Generating..."
              : generations.length > 0
                ? "Re-generate"
                : "Generate"}
          </Button>

          {aiTransform.status === SpecsImageProcessingStatus.ERROR &&
            aiTransform.errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {aiTransform.errorMessage}
              </Alert>
            )}

          {viewedGen && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {viewedGen.imageUrl === aiTransform.acceptedImageUrl
                  ? "Currently accepted"
                  : "Generation Preview"}
              </Typography>
              <Box
                component="img"
                src={resolvedViewedGenUrl}
                alt="AI result"
                sx={{
                  width: "100%",
                  maxHeight: 400,
                  objectFit: "contain",
                  borderRadius: "4px",
                  bgcolor: palette.grey.lightBackground,
                  border:
                    viewedGen.imageUrl === aiTransform.acceptedImageUrl
                      ? `2px solid ${palette.brand.primary}`
                      : "none",
                }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flex: 1, mr: 1 }}
                >
                  {viewedGen.prompt.slice(0, 150)}
                  {viewedGen.prompt.length > 150 ? "..." : ""}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    void acceptAiGeneration(viewedGen.id);
                  }}
                  disabled={viewedGen.imageUrl === aiTransform.acceptedImageUrl}
                >
                  {viewedGen.imageUrl === aiTransform.acceptedImageUrl
                    ? "Accepted"
                    : "Accept this"}
                </Button>
              </Stack>
            </Box>
          )}

          {generations.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="caption">
                  Generations ({generations.length})
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setAiTransform((current) => ({
                      ...current,
                      status: SpecsImageProcessingStatus.IDLE,
                      acceptedImageUrl: null,
                      errorMessage: undefined,
                      generations: [],
                    }));
                  }}
                >
                  Clear all
                </Button>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {generations.map((generation) => (
                  <Tooltip
                    key={generation.id}
                    title={generation.prompt.slice(0, 60)}
                  >
                    <Box
                      component="img"
                      src={resolveSpecsImageUrl(generation.imageUrl)}
                      alt={generation.prompt.slice(0, 30)}
                      onClick={() => {
                        setSelectedGenId(generation.id);
                        void acceptAiGeneration(generation.id);
                      }}
                      sx={{
                        width: 72,
                        height: 72,
                        objectFit: "contain",
                        borderRadius: "4px",
                        cursor: "pointer",
                        bgcolor: palette.grey.lightBackground,
                        border:
                          generation.imageUrl === aiTransform.acceptedImageUrl
                            ? `2px solid ${palette.brand.primary}`
                            : generation.id === (selectedGenId ?? latestGen?.id)
                              ? `2px solid ${palette.grey.darkerGrey}`
                              : "2px solid transparent",
                        "&:hover": { borderColor: palette.brand.primary },
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {hasPendingFilters ? (
            <Button
              variant="contained"
              onClick={() => {
                void applyFilteredImage();
              }}
              disabled={applyFiltersLoading}
            >
              {applyFiltersLoading ? "Applying..." : "Apply filters"}
            </Button>
          ) : null}
          <Button onClick={requestClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard unapplied filters?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            The image has filter changes that have not been applied.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscardOpen(false)}>Annuler</Button>
          <Button
            color="error"
            onClick={() => {
              setTransformations([]);
              setConfirmDiscardOpen(false);
              onClose();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
